import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { setLocale, type Locale } from "./i18n";
import { BlogIndex, BlogPostPage, BlogTeaser, applyBlogMeta } from "./blog/BlogPages";
import { getPostBySlug } from "./blog/posts";
import { AnimatePresence, motion, useReducedMotion, type Variants } from "motion/react";
import { marked } from "marked";
import { dpaSv } from "./legal/dpaSv";
import { dpaEn } from "./legal/dpaEn";
import { trustMd } from "./legal/trustMd";
import { StoreDownloadLinks } from "./components/StoreDownloadLinks";

const APP_URL = import.meta.env.VITE_APP_URL ?? "https://app.andrum.app";
const PRACTICE_URL = import.meta.env.VITE_PRACTICE_URL ?? "https://practice.andrum.app";
const GOOGLE_PLAY_URL =
  import.meta.env.VITE_GOOGLE_PLAY_URL ??
  "https://play.google.com/store/apps/details?id=app.andrum";
const APP_STORE_URL = import.meta.env.VITE_APP_STORE_URL?.trim() || undefined;

type Page =
  | "home"
  | "privacy"
  | "terms"
  | "safety"
  | "contact"
  | "blog"
  | "blog-post"
  | "practice"
  | "dpa"
  | "trust";

function pathToPage(path: string): Page {
  if (path.startsWith("/privacy")) return "privacy";
  if (path.startsWith("/terms")) return "terms";
  if (path.startsWith("/safety")) return "safety";
  if (path.startsWith("/contact")) return "contact";
  if (path.startsWith("/dpa")) return "dpa";
  if (path.startsWith("/trust")) return "trust";
  if (path === "/practice" || path.startsWith("/practice/")) return "practice";
  if (path === "/blog" || path === "/blog/") return "blog";
  if (path.startsWith("/blog/")) return "blog-post";
  return "home";
}

function blogSlugFromPath(path: string): string {
  const parts = path.split("/").filter(Boolean);
  // ["blog", "slug"]
  return parts[1] ?? "";
}

function navigate(page: Page, slug?: string) {
  let path = "/";
  if (page === "privacy") path = "/privacy";
  else if (page === "terms") path = "/terms";
  else if (page === "safety") path = "/safety";
  else if (page === "contact") path = "/contact";
  else if (page === "practice") path = "/practice";
  else if (page === "dpa") path = "/dpa";
  else if (page === "trust") path = "/trust";
  else if (page === "blog") path = "/blog";
  else if (page === "blog-post") path = `/blog/${slug ?? ""}`;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  // Always land at top of the new page (SPA keeps scroll otherwise).
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

const ease = [0.22, 1, 0.36, 1] as const;

const fadeUp: Variants = {
  hidden: { opacity: 1, y: 8 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease },
  },
};

const stagger: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.08, delayChildren: 0.06 },
  },
};

const pageFade: Variants = {
  initial: { opacity: 1, y: 0 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease },
  },
  exit: {
    opacity: 0.4,
    y: -6,
    transition: { duration: 0.18, ease },
  },
};

function scrollToId(id: string, reduce: boolean) {
  // Path only, do not force scroll-to-top before section scroll.
  if (window.location.pathname !== "/") {
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
      });
    });
  });
}

export function App() {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState<Page>(() => pathToPage(window.location.pathname));
  const [blogSlug, setBlogSlug] = useState(() =>
    blogSlugFromPath(window.location.pathname)
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);
  // Prefer reduced motion by default: avoids blank first paint (iOS Safari + motion).
  // Also force-reduce on coarse pointers (phones/tablets).
  const reduceMotion = useReducedMotion();
  const coarsePointer =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  const reduce = reduceMotion !== false || coarsePointer;
  const rawLng = i18n.language?.split("-")[0] ?? "sv";
  const locale = (
    rawLng === "en" || rawLng === "fr" || rawLng === "de" || rawLng === "ja"
      ? rawLng
      : "sv"
  ) as Locale;

  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      setPage(pathToPage(path));
      setBlogSlug(blogSlugFromPath(path));
      // Browser back/forward: also reset scroll for full pages.
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    // Safety net: any page change lands at top (covers first load + edge cases).
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [page, blogSlug]);

  useEffect(() => {
    if (page === "blog") {
      applyBlogMeta("index");
      return;
    }
    if (page === "blog-post") {
      const post = getPostBySlug(blogSlug);
      if (post) {
        applyBlogMeta(post);
        return;
      }
    }

    const titleKeys: Record<Page, string> = {
      home: "meta.homeTitle",
      privacy: "meta.privacyTitle",
      terms: "meta.termsTitle",
      safety: "meta.safetyTitle",
      contact: "meta.contactTitle",
      practice: "practice.metaTitle",
      dpa: "dpa.metaTitle",
      trust: "trust.metaTitle",
      blog: "blog.metaTitle",
      "blog-post": "blog.metaTitle",
    };
    document.title = t(titleKeys[page]);

    const description =
      page === "home"
        ? t("meta.homeDescription")
        : page === "practice"
          ? t("practice.metaDescription")
          : t(titleKeys[page]);
    const ogTitle =
      page === "home"
        ? t("meta.ogTitle")
        : page === "practice"
          ? t("practice.metaTitle")
          : t(titleKeys[page]);
    const ogDescription =
      page === "home"
        ? t("meta.ogDescription")
        : page === "practice"
          ? t("practice.metaDescription")
          : description;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", ogTitle);
    setMeta('meta[property="og:description"]', "content", ogDescription);
    setMeta('meta[name="twitter:title"]', "content", ogTitle);
    setMeta('meta[name="twitter:description"]', "content", ogDescription);

    const localeMap: Record<string, string> = {
      sv: "sv_SE",
      en: "en_US",
      de: "de_DE",
      fr: "fr_FR",
      ja: "ja_JP",
    };
    const lng = (i18n.language || "sv").split("-")[0] ?? "sv";
    setMeta('meta[property="og:locale"]', "content", localeMap[lng] ?? "sv_SE");
    document.documentElement.lang = lng;

    // JSON-LD for home + practice SEO
    const existing = document.getElementById("andrum-jsonld");
    if (existing) existing.remove();
    if (page === "home") {
      const faqCount = 8;
      const faqEntities = Array.from({ length: faqCount }, (_, i) => {
        const n = i + 1;
        return {
          "@type": "Question",
          name: t(`faq.q${n}`),
          acceptedAnswer: {
            "@type": "Answer",
            text: t(`faq.a${n}`),
          },
        };
      });
      const graph = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": "https://andrum.app/#organization",
            name: "Adam Hjort Consulting AB",
            alternateName: "andrum",
            legalName: "Adam Hjort Consulting AB",
            url: "https://andrum.app",
            email: "support@andrum.app",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Stockholm",
              addressCountry: "SE",
            },
            identifier: {
              "@type": "PropertyValue",
              name: "Swedish organization number",
              value: "559396-6749",
            },
          },
          {
            "@type": "SoftwareApplication",
            name: "andrum",
            applicationCategory: "HealthApplication",
            operatingSystem: "Web, iOS, Android",
            url: "https://andrum.app",
            description: t("meta.homeDescription"),
            publisher: { "@id": "https://andrum.app/#organization" },
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "SEK",
              description: "Free core during beta",
            },
          },
          {
            "@type": "WebSite",
            name: "andrum",
            url: "https://andrum.app",
            description: t("meta.homeDescription"),
            publisher: { "@id": "https://andrum.app/#organization" },
          },
          {
            "@type": "FAQPage",
            mainEntity: faqEntities,
          },
        ],
      };
      const script = document.createElement("script");
      script.id = "andrum-jsonld";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(graph);
      document.head.appendChild(script);
    }
    if (page === "practice") {
      const faqEntities = Array.from({ length: 5 }, (_, i) => {
        const n = i + 1;
        return {
          "@type": "Question",
          name: t(`practice.faq${n}q`),
          acceptedAnswer: {
            "@type": "Answer",
            text: t(`practice.faq${n}a`),
          },
        };
      });
      const graph = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "SoftwareApplication",
            name: "andrum Practice",
            applicationCategory: "BusinessApplication",
            applicationSubCategory: "Healthcare",
            operatingSystem: "Web",
            url: "https://andrum.app/practice",
            description: t("practice.metaDescription"),
            audience: {
              "@type": "Audience",
              audienceType: "Psychologists, therapists, and clinics",
            },
            offers: [
              {
                "@type": "Offer",
                name: "Solo",
                price: "499",
                priceCurrency: "SEK",
                priceSpecification: {
                  "@type": "UnitPriceSpecification",
                  price: "499",
                  priceCurrency: "SEK",
                  billingDuration: "P1M",
                },
              },
              {
                "@type": "Offer",
                name: "Team",
                price: "1790",
                priceCurrency: "SEK",
              },
            ],
          },
          {
            "@type": "WebPage",
            name: t("practice.metaTitle"),
            url: "https://andrum.app/practice",
            description: t("practice.metaDescription"),
            inLanguage: lng,
          },
          {
            "@type": "FAQPage",
            mainEntity: faqEntities,
          },
        ],
      };
      const script = document.createElement("script");
      script.id = "andrum-jsonld";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(graph);
      document.head.appendChild(script);
    }
  }, [page, blogSlug, t, i18n.language]);

  return (
    <div className="shell">
      <header
        className={`header-nav${scrolled ? " is-scrolled" : ""}${menuOpen ? " menu-open" : ""}`}
      >
        <div className="container header-nav-inner">
          <div className="nav nav-glass">
            <a
              className="brand"
              href="/"
              onClick={(e) => {
                e.preventDefault();
                setMenuOpen(false);
                navigate("home");
              }}
            >
              <img
                className="brand-logo"
                src="/logo-horizontal.svg"
                alt="andrum"
                width={96}
                height={22}
              />
            </a>

            {/* Desktop nav */}
            <nav className="nav-links nav-links-desktop" aria-label={t("meta.navAria")}>
              <a
                href="#how"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId("how", !!reduce);
                }}
              >
                {t("nav.how")}
              </a>
              <a
                href="#why"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId("why", !!reduce);
                }}
              >
                {t("nav.why")}
              </a>
              <a
                href="#pricing"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId("pricing", !!reduce);
                }}
              >
                {t("nav.plus")}
              </a>
              <a
                href="#science"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId("science", !!reduce);
                }}
              >
                {t("nav.science")}
              </a>
              <a
                href="#faq"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId("faq", !!reduce);
                }}
              >
                {t("nav.faq")}
              </a>
              <a
                href="/blog"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("blog");
                }}
              >
                {t("nav.blog")}
              </a>
              <a
                href="/practice"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("practice");
                }}
              >
                {t("nav.practice")}
              </a>
              <div
                className="lang-switch lang-switch-desktop"
                role="group"
                aria-label={t("meta.langAria")}
              >
                {(["sv", "en", "fr", "de", "ja"] as const).map((code) => (
                  <button
                    key={code}
                    type="button"
                    className={locale === code ? "lang-active" : ""}
                    onClick={() => setLocale(code)}
                  >
                    {t(`lang.${code}`)}
                  </button>
                ))}
              </div>
              <a className="btn btn-primary" href={APP_URL}>
                {t("nav.openApp")}
              </a>
            </nav>

            {/* Mobile: CTA + hamburger */}
            <div className="nav-mobile-actions">
              <a className="btn btn-primary btn-nav-mobile" href={APP_URL}>
                {t("nav.openApp")}
              </a>
              <button
                type="button"
                className={`nav-burger${menuOpen ? " is-open" : ""}`}
                aria-expanded={menuOpen}
                aria-controls="mobile-menu"
                aria-label={menuOpen ? t("nav.menuClose") : t("nav.menuOpen")}
                onClick={() => setMenuOpen((o) => !o)}
              >
                <span className="nav-burger-lines" aria-hidden>
                  <span />
                  <span />
                  <span />
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer, always mounted for transitions; z-index above main content */}
      <div
        className={`nav-drawer-backdrop${menuOpen ? " is-open" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden={!menuOpen}
      />
      <nav
        id="mobile-menu"
        className={`nav-drawer${menuOpen ? " is-open" : ""}`}
        aria-label={t("meta.navAria")}
        aria-hidden={!menuOpen}
      >
        <a
          href="#how"
          onClick={(e) => {
            e.preventDefault();
            setMenuOpen(false);
            scrollToId("how", !!reduce);
          }}
        >
          {t("nav.how")}
        </a>
        <a
          href="#why"
          onClick={(e) => {
            e.preventDefault();
            setMenuOpen(false);
            scrollToId("why", !!reduce);
          }}
        >
          {t("nav.why")}
        </a>
        <a
          href="#pricing"
          onClick={(e) => {
            e.preventDefault();
            setMenuOpen(false);
            scrollToId("pricing", !!reduce);
          }}
        >
          {t("nav.plus")}
        </a>
        <a
          href="#science"
          onClick={(e) => {
            e.preventDefault();
            setMenuOpen(false);
            scrollToId("science", !!reduce);
          }}
        >
          {t("nav.science")}
        </a>
        <a
          href="#faq"
          onClick={(e) => {
            e.preventDefault();
            setMenuOpen(false);
            scrollToId("faq", !!reduce);
          }}
        >
          {t("nav.faq")}
        </a>
        <a
          href="/blog"
          onClick={(e) => {
            e.preventDefault();
            setMenuOpen(false);
            navigate("blog");
          }}
        >
          {t("nav.blog")}
        </a>
        <a
          href="/practice"
          onClick={(e) => {
            e.preventDefault();
            setMenuOpen(false);
            navigate("practice");
          }}
        >
          {t("nav.practice")}
        </a>
        <a
          href="/privacy"
          onClick={(e) => {
            e.preventDefault();
            setMenuOpen(false);
            navigate("privacy");
          }}
        >
          {t("nav.privacy")}
        </a>
        <label className="lang-select-wrap nav-drawer-lang">
          <span className="visually-hidden">{t("meta.langAria")}</span>
          <select
            className="lang-select"
            value={locale}
            aria-label={t("meta.langAria")}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "sv" || v === "en" || v === "fr" || v === "de" || v === "ja") {
                setLocale(v);
              }
            }}
          >
            {(["sv", "en", "fr", "de", "ja"] as const).map((code) => (
              <option key={code} value={code}>
                {t(`lang.${code}`)}
              </option>
            ))}
          </select>
        </label>
        <a className="btn btn-primary nav-drawer-cta" href={APP_URL}>
          {t("nav.openApp")}
        </a>
      </nav>

      <main className="container">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${page}-${i18n.language}`}
            {...(reduce
              ? { initial: false }
              : {
                  variants: pageFade,
                  initial: "initial" as const,
                  animate: "animate" as const,
                  exit: "exit" as const,
                })}
          >
            {page === "home" ? <Home reduce={!!reduce} /> : null}
            {page === "practice" ? <PracticeLanding /> : null}
            {page === "dpa" ? <DpaPage /> : null}
            {page === "trust" ? <TrustPage /> : null}
            {page === "privacy" ? <Privacy /> : null}
            {page === "terms" ? <Terms /> : null}
            {page === "safety" ? <Safety /> : null}
            {page === "contact" ? <Contact /> : null}
            {page === "blog" ? (
              <BlogIndex
                onOpenPost={(slug) => navigate("blog-post", slug)}
                onHome={() => navigate("home")}
              />
            ) : null}
            {page === "blog-post" ? (
              <BlogPostPage
                slug={blogSlug}
                onBack={() => navigate("blog")}
                onHome={() => navigate("home")}
              />
            ) : null}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="footer">
        <div className="container footer-inner">
          <div className="footer-brand">
            <img
              className="footer-logo"
              src="/logo-horizontal.svg"
              alt="andrum"
              width={88}
              height={20}
            />
            <p className="footer-legal">
              © {new Date().getFullYear()} Adam Hjort Consulting AB · Org.nr 559396-6749 ·
              Stockholm
            </p>
          </div>
          <div className="footer-links">
            <a
              href="/privacy"
              onClick={(e) => {
                e.preventDefault();
                navigate("privacy");
              }}
            >
              {t("footer.privacy")}
            </a>
            <a
              href="/terms"
              onClick={(e) => {
                e.preventDefault();
                navigate("terms");
              }}
            >
              {t("footer.terms")}
            </a>
            <a
              href="/safety"
              onClick={(e) => {
                e.preventDefault();
                navigate("safety");
              }}
            >
              {t("footer.safety")}
            </a>
            <a
              href="/contact"
              onClick={(e) => {
                e.preventDefault();
                navigate("contact");
              }}
            >
              {t("footer.contact")}
            </a>
            <a
              href="/practice"
              onClick={(e) => {
                e.preventDefault();
                navigate("practice");
              }}
            >
              {t("nav.practice")}
            </a>
            <a
              href="/dpa"
              onClick={(e) => {
                e.preventDefault();
                navigate("dpa");
              }}
            >
              DPA
            </a>
            <a
              href="/trust"
              onClick={(e) => {
                e.preventDefault();
                navigate("trust");
              }}
            >
              Trust
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function LegalMarkdown({ source }: { source: string }) {
  const html = useMemo(() => {
    marked.setOptions({
      gfm: true,
      breaks: false,
    });
    return marked.parse(source, { async: false }) as string;
  }, [source]);

  return (
    <div
      className="legal-md"
      // Trusted static legal copy shipped in the repo (not user-generated).
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function DpaPage() {
  const { i18n } = useTranslation();
  const [lang, setLang] = useState<"sv" | "en">(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("lang");
      if (q === "en") return "en";
    } catch {
      // ignore
    }
    return i18n.language?.startsWith("en") ? "en" : "sv";
  });
  const text = lang === "en" ? dpaEn : dpaSv;

  return (
    <section className="section legal" style={{ paddingTop: 40, maxWidth: 760 }}>
      <div className="row" style={{ gap: 8, marginBottom: 16, display: "flex" }}>
        <button
          type="button"
          className={lang === "sv" ? "btn btn-primary" : "btn btn-ghost"}
          onClick={() => {
            setLang("sv");
            window.history.replaceState({}, "", "/dpa");
          }}
        >
          Svenska
        </button>
        <button
          type="button"
          className={lang === "en" ? "btn btn-primary" : "btn btn-ghost"}
          onClick={() => {
            setLang("en");
            window.history.replaceState({}, "", "/dpa?lang=en");
          }}
        >
          English
        </button>
        <a
          className="btn btn-ghost"
          href="/trust"
          onClick={(e) => {
            e.preventDefault();
            navigate("trust");
          }}
        >
          Trust center
        </a>
      </div>
      <LegalMarkdown source={text} />
      <p className="muted" style={{ marginTop: 32, fontSize: 13 }}>
        Version practice_dpa_v1 · <a href={PRACTICE_URL}>Practice Console</a>
      </p>
    </section>
  );
}

function TrustPage() {
  return (
    <section className="section legal" style={{ paddingTop: 40, maxWidth: 760 }}>
      <LegalMarkdown source={trustMd} />
      <p style={{ marginTop: 24 }}>
        <a
          href="/dpa"
          onClick={(e) => {
            e.preventDefault();
            navigate("dpa");
          }}
        >
          Öppna DPA (SV)
        </a>
        {" · "}
        <a
          href="/dpa?lang=en"
          onClick={(e) => {
            e.preventDefault();
            navigate("dpa");
            window.history.replaceState({}, "", "/dpa?lang=en");
          }}
        >
          DPA (EN)
        </a>
        {" · "}
        <a
          href="/practice"
          onClick={(e) => {
            e.preventDefault();
            navigate("practice");
          }}
        >
          Practice
        </a>
      </p>
    </section>
  );
}

function PracticeLanding() {
  const { t } = useTranslation();
  const benefits = [1, 2, 3, 4].map((n) => ({
    title: t(`practice.f${n}t`),
    body: t(`practice.f${n}b`),
  }));
  const steps = [1, 2, 3].map((n) => ({
    n: String(n),
    title: t(`practice.s${n}t`),
    body: t(`practice.s${n}b`),
  }));
  const faqs = [1, 2, 3, 4, 5].map((n) => ({
    q: t(`practice.faq${n}q`),
    a: t(`practice.faq${n}a`),
  }));

  return (
    <div className="practice-page">
      <section className="practice-hero">
        <p className="eyebrow">{t("practice.eyebrow")}</p>
        <h1>{t("practice.title")}</h1>
        <p className="practice-lead">{t("practice.lead")}</p>
        <div className="hero-actions">
          <a className="btn btn-primary btn-lg" href={PRACTICE_URL}>
            {t("practice.cta")}
          </a>
          <a className="btn btn-ghost btn-lg" href="#practice-pricing">
            {t("practice.ctaPrices")}
          </a>
        </div>
        <ul className="practice-trust-pills" aria-label={t("practice.trustAria")}>
          <li>{t("practice.pill1")}</li>
          <li>{t("practice.pill2")}</li>
          <li>{t("practice.pill3")}</li>
        </ul>
      </section>

      <section className="practice-section">
        <div className="section-head practice-section-head">
          <h2>{t("practice.forWhomTitle")}</h2>
          <p className="section-sub">{t("practice.forWhomSub")}</p>
        </div>
        <div className="practice-benefit-grid">
          {benefits.map((b) => (
            <article key={b.title} className="card practice-card">
              <h3>{b.title}</h3>
              <p>{b.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="practice-section">
        <div className="section-head practice-section-head">
          <h2>{t("practice.howTitle")}</h2>
          <p className="section-sub">{t("practice.howSub")}</p>
        </div>
        <ol className="practice-steps">
          {steps.map((s) => (
            <li key={s.n} className="card practice-step">
              <span className="practice-step-n" aria-hidden>
                {s.n}
              </span>
              <div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="practice-section" id="practice-pricing">
        <div className="section-head practice-section-head">
          <h2>{t("practice.pricingTitle")}</h2>
          <p className="section-sub">{t("practice.pricingSub")}</p>
        </div>
        <div className="practice-pricing-grid">
          <article className="card practice-price-card practice-price-featured">
            <p className="practice-price-label">{t("practice.soloBadge")}</p>
            <h3>{t("practice.soloTitle")}</h3>
            <p className="practice-price-amount">
              <strong>499 kr</strong>
              <span>/{t("practice.month")}</span>
            </p>
            <p className="practice-price-note">{t("practice.soloYear")}</p>
            <ul className="practice-price-list">
              <li>{t("practice.soloL1")}</li>
              <li>{t("practice.soloL2")}</li>
              <li>{t("practice.soloL3")}</li>
            </ul>
            <a className="btn btn-primary" href={PRACTICE_URL}>
              {t("practice.ctaSolo")}
            </a>
          </article>
          <article className="card practice-price-card">
            <h3>{t("practice.teamTitle")}</h3>
            <p className="practice-price-amount">
              <strong>1 790 kr</strong>
              <span>/{t("practice.month")}</span>
            </p>
            <p className="practice-price-note">{t("practice.teamYear")}</p>
            <ul className="practice-price-list">
              <li>{t("practice.teamL1")}</li>
              <li>{t("practice.teamL2")}</li>
              <li>{t("practice.teamL3")}</li>
            </ul>
            <a className="btn btn-ghost" href={PRACTICE_URL}>
              {t("practice.ctaTeam")}
            </a>
          </article>
          <article className="card practice-price-card">
            <h3>{t("practice.clinicTitle")}</h3>
            <p className="practice-price-amount">
              <strong>{t("practice.from")} 3 490 kr</strong>
              <span>/{t("practice.month")}</span>
            </p>
            <p className="practice-price-note">{t("practice.clinicNote")}</p>
            <ul className="practice-price-list">
              <li>{t("practice.clinicL1")}</li>
              <li>{t("practice.clinicL2")}</li>
              <li>{t("practice.clinicL3")}</li>
            </ul>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => navigate("contact")}
            >
              {t("practice.ctaClinic")}
            </button>
          </article>
        </div>
        <p className="practice-vat">{t("practice.vatNote")}</p>
      </section>

      <section className="practice-section">
        <div className="section-head practice-section-head">
          <h2>{t("practice.faqTitle")}</h2>
        </div>
        <div className="faq-list practice-faq">
          {faqs.map((item) => (
            <details key={item.q} className="faq-item">
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="practice-cta-band">
        <div>
          <h2>{t("practice.ctaBandTitle")}</h2>
          <p>{t("practice.ctaBandBody")}</p>
        </div>
        <a className="btn btn-primary btn-lg" href={PRACTICE_URL}>
          {t("practice.cta")}
        </a>
      </section>

      <p className="practice-foot">
        {t("practice.disclaimer")}{" "}
        <a
          href="/dpa"
          onClick={(e) => {
            e.preventDefault();
            navigate("dpa");
          }}
        >
          {t("practice.dpaLink")}
        </a>
        {" · "}
        <a
          href="/trust"
          onClick={(e) => {
            e.preventDefault();
            navigate("trust");
          }}
        >
          {t("practice.trustLink")}
        </a>
      </p>
    </div>
  );
}

function Home({ reduce }: { reduce: boolean }) {
  const { t } = useTranslation();

  const howCards = [
    { icon: "◎", title: t("how.c1t"), body: t("how.c1b") },
    { icon: "✧", title: t("how.c2t"), body: t("how.c2b") },
    { icon: "◉", title: t("how.c3t"), body: t("how.c3b") },
  ];

  const features = [t("why.f1"), t("why.f2"), t("why.f3"), t("why.f4"), t("why.f5")];

  const scienceCards = [
    { title: t("science.c1t"), body: t("science.c1b") },
    { title: t("science.c2t"), body: t("science.c2b") },
    { title: t("science.c3t"), body: t("science.c3b") },
  ];

  const faqItems = [1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({
    q: t(`faq.q${n}`),
    a: t(`faq.a${n}`),
  }));

  return (
    <>
      <section className="hero">
        <motion.div
          {...(reduce
            ? { initial: false }
            : {
                variants: stagger,
                initial: "hidden" as const,
                animate: "show" as const,
              })}
        >
          <motion.p className="eyebrow" variants={fadeUp}>
            {t("hero.eyebrow")}
          </motion.p>
          <motion.h1 variants={fadeUp}>{t("hero.title")}</motion.h1>
          <motion.p className="lead" variants={fadeUp}>
            {t("hero.lead")}
          </motion.p>
          <motion.div className="hero-actions" variants={fadeUp}>
            <motion.a
              className="btn btn-primary btn-lg"
              href={APP_URL}
              {...(!reduce
                ? {
                    whileHover: { scale: 1.03, y: -2 },
                    whileTap: { scale: 0.98 },
                  }
                : {})}
            >
              {t("hero.cta")}
            </motion.a>
            <a className="btn btn-ghost btn-lg" href="#how">
              {t("hero.how")}
            </a>
          </motion.div>
          <motion.div variants={fadeUp}>
            <StoreDownloadLinks
              googlePlayUrl={GOOGLE_PLAY_URL}
              appStoreUrl={APP_STORE_URL}
              labels={{
                group: t("store.group"),
                availableOn: t("store.availableOn"),
                googlePlay: t("store.googlePlay"),
                appStore: t("store.appStore"),
                comingSoon: t("store.comingSoon"),
              }}
            />
          </motion.div>
          <motion.div className="trust-row" variants={fadeUp}>
            <span className="trust-pill">{t("hero.trust1")}</span>
            <span className="trust-pill">{t("hero.trust2")}</span>
            <span className="trust-pill">{t("hero.trust3")}</span>
            <span className="trust-pill">{t("hero.trust4")}</span>
            <span className="trust-pill">{t("hero.trust5")}</span>
          </motion.div>
        </motion.div>

        <Showcase reduce={reduce} />
      </section>

      <section className="section" id="how">
        <motion.div
          className="section-head"
          initial={reduce ? false : { opacity: 1, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, ease }}
        >
          <h2>{t("how.title")}</h2>
          <p className="section-sub">{t("how.sub")}</p>
        </motion.div>

        <div className="grid-3">
          {howCards.map((item, i) => (
            <motion.article
              key={item.title}
              className="card"
              initial={reduce ? false : { opacity: 1, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: i * 0.08, ease }}
              {...(!reduce ? { whileHover: { y: -4 } } : {})}
            >
              <div className="card-icon" aria-hidden>
                {item.icon}
              </div>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="section" id="why">
        <div className="grid-2">
          <motion.div
            initial={reduce ? false : { opacity: 1, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease }}
          >
            <h2>{t("why.title")}</h2>
            <p className="section-sub" style={{ marginTop: 12 }}>
              {t("why.sub")}
            </p>
            <ul className="feature-list" style={{ marginTop: 24 }}>
              {features.map((text) => (
                <li key={text}>
                  <span className="tick" aria-hidden>
                    ✓
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="card plus-card"
            initial={reduce ? false : { opacity: 1, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease }}
          >
            <p className="eyebrow">{t("why.plusEyebrow")}</p>
            <h3 className="plus-card-title">{t("why.plusTitle")}</h3>
            <p>{t("why.plusBody")}</p>
            <a className="btn btn-primary plus-card-cta" href={`${APP_URL}/app/paywall`}>
              {t("why.plusCta")}
            </a>
          </motion.div>
        </div>
      </section>

      <section className="section" id="pricing">
        <motion.div
          className="section-head"
          initial={reduce ? false : { opacity: 1, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, ease }}
        >
          <h2>{t("pricing.title")}</h2>
          <p className="section-sub">{t("pricing.sub")}</p>
        </motion.div>

        <div className="pricing-grid">
          <motion.article
            className="card pricing-card"
            initial={reduce ? false : { opacity: 1, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, ease }}
          >
            <p className="pricing-label">{t("pricing.free")}</p>
            <h3>{t("pricing.freeTitle")}</h3>
            <p className="pricing-price">{t("pricing.freePrice")}</p>
            <ul className="pricing-list">
              <li>{t("pricing.free1")}</li>
              <li>{t("pricing.free2")}</li>
              <li>{t("pricing.free3")}</li>
              <li>{t("pricing.free4")}</li>
              <li>{t("pricing.free5")}</li>
            </ul>
            <a className="btn btn-ghost" href={APP_URL}>
              {t("pricing.freeCta")}
            </a>
          </motion.article>

          <motion.article
            className="card pricing-card pricing-card-featured"
            initial={reduce ? false : { opacity: 1, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.08, ease }}
          >
            <p className="pricing-label">{t("pricing.plus")}</p>
            <h3>{t("pricing.plusTitle")}</h3>
            <p className="pricing-price">
              {t("pricing.plusPrice")}
              <span className="pricing-note">{t("pricing.plusNote")}</span>
            </p>
            <ul className="pricing-list">
              <li>{t("pricing.plus1")}</li>
              <li>{t("pricing.plus2")}</li>
              <li>{t("pricing.plus3")}</li>
              <li>{t("pricing.plus4")}</li>
              <li>{t("pricing.plus5")}</li>
            </ul>
            <a className="btn btn-primary" href={`${APP_URL}/app/paywall`}>
              {t("pricing.plusCta")}
            </a>
          </motion.article>
        </div>
      </section>

      <section className="section" id="science">
        <motion.div
          className="section-head"
          initial={reduce ? false : { opacity: 1, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, ease }}
        >
          <h2>{t("science.title")}</h2>
          <p className="section-sub">{t("science.sub")}</p>
        </motion.div>
        <div className="grid-3">
          {scienceCards.map((item, i) => (
            <motion.article
              key={item.title}
              className="card"
              initial={reduce ? false : { opacity: 1, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: i * 0.08, ease }}
            >
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <BlogTeaser
        onOpenBlog={() => navigate("blog")}
        onOpenPost={(slug) => navigate("blog-post", slug)}
      />

      <section className="section" id="faq">
        <motion.div
          className="section-head"
          initial={reduce ? false : { opacity: 1, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, ease }}
        >
          <h2>{t("faq.title")}</h2>
          <p className="section-sub">{t("faq.sub")}</p>
        </motion.div>
        <div className="faq-list">
          {faqItems.map((item, i) => (
            <motion.details
              key={item.q}
              className="faq-item"
              initial={reduce ? false : { opacity: 1, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04, ease }}
            >
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </motion.details>
          ))}
        </div>
      </section>

      <section className="section practice-home-band" id="for-therapists">
        <div className="practice-home-inner card">
          <div>
            <p className="eyebrow">{t("homePractice.eyebrow")}</p>
            <h2>{t("homePractice.title")}</h2>
            <p className="section-sub">{t("homePractice.body")}</p>
          </div>
          <div className="hero-actions">
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={() => navigate("practice")}
            >
              {t("homePractice.cta")}
            </button>
            <a className="btn btn-ghost btn-lg" href={PRACTICE_URL}>
              {t("homePractice.ctaTrial")}
            </a>
          </div>
        </div>
      </section>

      <motion.section
        className="cta-band"
        initial={reduce ? false : { opacity: 1, scale: 0.98 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease }}
      >
        <div>
          <h2>{t("cta.title")}</h2>
          <p>{t("cta.body")}</p>
        </div>
        <motion.a
          className="btn btn-primary btn-lg"
          href={APP_URL}
          {...(!reduce ? { whileHover: { scale: 1.04 }, whileTap: { scale: 0.98 } } : {})}
        >
          {t("cta.button")}
        </motion.a>
      </motion.section>

      <p className="soft-note">
        {t("crisis.before")} <strong>112</strong>
        {t("crisis.after112")}{" "}
        <a href="https://mind.se" target="_blank" rel="noreferrer">
          mind.se
        </a>{" "}
        {t("crisis.afterMind")}{" "}
        <a
          href="/safety"
          onClick={(e) => {
            e.preventDefault();
            navigate("safety");
          }}
        >
          {t("crisis.safetyLink")}
        </a>
        .
      </p>
    </>
  );
}

function Showcase({ reduce }: { reduce: boolean }) {
  const { t } = useTranslation();
  return (
    <motion.div
      className="showcase"
      initial={reduce ? false : { opacity: 1, scale: 0.96, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.7, ease, delay: 0.15 }}
    >
      <motion.div
        className="showcase-glow showcase-glow-a"
        {...(!reduce
          ? {
              animate: { x: [0, 18, 0], y: [0, 12, 0], scale: [1, 1.08, 1] },
              transition: {
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut" as const,
              },
            }
          : {})}
      />
      <motion.div
        className="showcase-glow showcase-glow-b"
        {...(!reduce
          ? {
              animate: { x: [0, -14, 0], y: [0, -10, 0], scale: [1, 1.1, 1] },
              transition: {
                duration: 12,
                repeat: Infinity,
                ease: "easeInOut" as const,
              },
            }
          : {})}
      />
      <div className="showcase-inner">
        <div className="showcase-logo-wrap">
          <img
            className="showcase-logo"
            src="/logo-horizontal.svg"
            alt="andrum"
            width={100}
            height={23}
          />
        </div>
        <motion.div
          className="phone-card"
          initial={reduce ? false : { opacity: 1, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.45, ease }}
        >
          <strong>{t("showcase.checkIn")}</strong>
          <span>{t("showcase.checkInSub")}</span>
          <div className="mood-row" aria-hidden>
            {["🙂", "😐", "😌", "✨"].map((emoji, i) => (
              <motion.div
                key={emoji}
                className={`mood-dot${i === 3 ? " active" : ""}`}
                {...(!reduce && i === 3
                  ? {
                      animate: { scale: [1, 1.08, 1] },
                      transition: { duration: 2.4, repeat: Infinity },
                    }
                  : {})}
              >
                {emoji}
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="phone-card"
          initial={reduce ? false : { opacity: 1, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.48, duration: 0.45, ease }}
        >
          <strong>{t("showcase.exp")}</strong>
          <span>{t("showcase.expSub")}</span>
          <span className="phone-meta">{t("showcase.expMeta")}</span>
        </motion.div>

        <motion.div
          className="phone-card"
          initial={reduce ? false : { opacity: 1, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.56, duration: 0.45, ease }}
        >
          <strong>{t("showcase.insight")}</strong>
          <span>{t("showcase.insightSub")}</span>
        </motion.div>

        <motion.div
          className="phone-card"
          initial={reduce ? false : { opacity: 1, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.68, duration: 0.45, ease }}
        >
          <strong>{t("showcase.coach")}</strong>
          <span>{t("showcase.coachSub")}</span>
        </motion.div>
      </div>
    </motion.div>
  );
}

function Privacy() {
  const { t } = useTranslation();
  return (
    <article className="legal">
      <h1>{t("privacy.title")}</h1>
      <p>{t("privacy.updated")}</p>
      <p>{t("privacy.intro")}</p>
      <h2>{t("privacy.controllerTitle")}</h2>
      <p>{t("privacy.controllerBody")}</p>
      <h2>{t("privacy.dataTitle")}</h2>
      <ul>
        <li>{t("privacy.data1")}</li>
        <li>{t("privacy.data2")}</li>
        <li>{t("privacy.data3")}</li>
        <li>{t("privacy.data4")}</li>
      </ul>
      <h2>{t("privacy.purposeTitle")}</h2>
      <p>{t("privacy.purposeBody")}</p>
      <h2>{t("privacy.rightsTitle")}</h2>
      <p>{t("privacy.rightsBody")}</p>
      <h2>{t("privacy.contactTitle")}</h2>
      <p>
        {t("privacy.contactBody").replace(/\.$/, "")}{" "}
        <a
          href="/contact"
          onClick={(e) => {
            e.preventDefault();
            navigate("contact");
          }}
        >
          {t("footer.contact").toLowerCase()}
        </a>
        .
      </p>
    </article>
  );
}

function Terms() {
  const { t } = useTranslation();
  return (
    <article className="legal">
      <h1>{t("terms.title")}</h1>
      <p>{t("terms.updated")}</p>
      <h2>{t("terms.providerTitle")}</h2>
      <p>{t("terms.providerBody")}</p>
      <h2>{t("terms.serviceTitle")}</h2>
      <p>{t("terms.serviceBody")}</p>
      <h2>{t("terms.accountTitle")}</h2>
      <p>{t("terms.accountBody")}</p>
      <h2>{t("terms.subTitle")}</h2>
      <p>{t("terms.subBody")}</p>
      <h2>{t("terms.useTitle")}</h2>
      <p>{t("terms.useBody")}</p>
    </article>
  );
}

function Safety() {
  const { t } = useTranslation();
  return (
    <article className="legal">
      <h1>{t("safety.title")}</h1>
      <p>{t("safety.intro")}</p>
      <h2>{t("safety.crisisTitle")}</h2>
      <ul>
        <li>
          <strong>112</strong>
        </li>
        <li>
          <strong>90101</strong> ·{" "}
          <a href="https://mind.se" target="_blank" rel="noreferrer">
            mind.se
          </a>
        </li>
      </ul>
      <h2>{t("safety.appTitle")}</h2>
      <p>{t("safety.appBody")}</p>
    </article>
  );
}

function Contact() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [topic, setTopic] = useState<"support" | "privacy">("support");
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error" | "rate">(
    "idle"
  );

  const apiBase =
    (import.meta as ImportMeta & { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ??
    "https://api.andrum.app";

  const submit = async () => {
    setStatus("sending");
    try {
      const channel = topic === "privacy" ? "privacy@andrum.app" : "support@andrum.app";
      const res = await fetch(`${apiBase}/api/v1/public/support`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: name.trim() || undefined,
          email: email.trim(),
          message: message.trim(),
          channel,
        }),
      });
      if (res.status === 429) {
        setStatus("rate");
        return;
      }
      if (!res.ok) {
        setStatus("error");
        return;
      }
      setStatus("ok");
      setMessage("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <article className="legal contact-page">
      <h1>{t("contact.title")}</h1>
      <p>
        {t("contact.body")} <a href="mailto:support@andrum.app">support@andrum.app</a>
        {" · "}
        <a href="mailto:privacy@andrum.app">privacy@andrum.app</a>
      </p>
      <p className="contact-operator">{t("company.operatorLine")}</p>
      <p className="contact-lead">{t("contact.formLead")}</p>
      <form
        className="contact-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="contact-field">
          <span>{t("contact.topic")}</span>
          <select
            value={topic}
            onChange={(e) =>
              setTopic(e.target.value === "privacy" ? "privacy" : "support")
            }
          >
            <option value="support">{t("contact.topicSupport")}</option>
            <option value="privacy">{t("contact.topicPrivacy")}</option>
          </select>
        </label>
        <label className="contact-field">
          <span>{t("contact.name")}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            maxLength={120}
          />
        </label>
        <label className="contact-field">
          <span>{t("contact.email")}</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            maxLength={320}
          />
        </label>
        <label className="contact-field">
          <span>{t("contact.message")}</span>
          <textarea
            required
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            minLength={5}
            maxLength={4000}
          />
        </label>
        <button
          type="submit"
          className="btn btn-primary contact-submit"
          disabled={status === "sending" || message.trim().length < 5}
        >
          {status === "sending" ? t("contact.sending") : t("contact.send")}
        </button>
        {status === "ok" ? (
          <p className="contact-status contact-status-ok">{t("contact.sent")}</p>
        ) : null}
        {status === "error" ? (
          <p className="contact-status contact-status-err">{t("contact.error")}</p>
        ) : null}
        {status === "rate" ? (
          <p className="contact-status contact-status-err">{t("contact.rateLimited")}</p>
        ) : null}
      </form>
      <p className="contact-app-link">
        {t("contact.openApp")} <a href={APP_URL}>{APP_URL.replace("https://", "")}</a>
      </p>
    </article>
  );
}
